/**
 * Kontext-spezifische Quick-Prompts & Slash-Befehle (Text-Templates für die Eingabe).
 *
 * Vorlagen, die auf „: " enden, setzt KIAN fort, sobald der Techniker den Rest ergänzt hat —
 * Ticket anlegen/ändern bereitet KIAN als Entwurf vor, der in der Karte bestätigt wird.
 */

export interface SlashCommandDef {
  cmd: string;
  label: string;
  template: string;
  /** Wird mit der Nachricht ans Backend geschickt (z. B. forceSimilar für /similar) */
  hints?: Record<string, unknown>;
}

export interface EntityPromptCatalog {
  quickPrompts: string[];
  slash: SlashCommandDef[];
}

const ticketSlash: SlashCommandDef[] = [
  { cmd: 'summary', label: 'Zusammenfassen', template: 'Fasse dieses Ticket kurz für einen Kollegen zusammen (Status, Problem, nächste Schritte).' },
  { cmd: 'next', label: 'Nächste Schritte', template: 'Welche sinnvollen nächsten 3 Schritte empfiehlst du für dieses Ticket?' },
  { cmd: 'note', label: 'Kundennotiz', template: 'Entwurf einer kurzen internen Kundennotiz (sachlich, höflich):' },
  { cmd: 'kb', label: 'Wissensbasis', template: 'Welche allgemeinen Hinweise aus der Wissensbasis könnten hier passen? (ohne Halluzinationen)' },
  {
    cmd: 'similar',
    label: 'Ähnliche Fälle',
    template: 'Welche ähnlichen Fehlermuster oder Lösungen könnten hier relevant sein?',
    hints: { forceSimilar: true },
  },
  { cmd: 'draft-email', label: 'E-Mail-Entwurf', template: 'Entwurf einer kurzen E-Mail an den Kunden (neutral, professionell):' },
];

/** Ticket-Aktionen am Ticket selbst — KIAN bereitet sie als Entwurf vor (prepare_ticket_update). */
const ticketActionSlash: SlashCommandDef[] = [
  { cmd: 'comment', label: 'Kommentar', template: 'Schreib diesen internen Kommentar ans Ticket: ' },
  { cmd: 'wait', label: 'Wartet auf …', template: 'Setze das Ticket auf „wartet" — Blocker: wartet auf ' },
  { cmd: 'close', label: 'Schließen', template: 'Schließe das Ticket mit diesem Abschlusskommentar: ' },
  { cmd: 'assign', label: 'Mir zuweisen', template: 'Weise mir das Ticket zu und setze es auf „in Bearbeitung".' },
];

const createTicketSlash: SlashCommandDef = {
  cmd: 'ticket',
  label: 'Ticket anlegen',
  template: 'Leg ein Ticket an: ',
};

/** Anleitung für die Wissensdatenbank — KIAN bereitet sie als Entwurf vor (prepare_knowledge_article). */
const wikiSlash: SlashCommandDef = {
  cmd: 'anleitung',
  label: 'Anleitung fürs Wiki',
  template: 'Mach aus diesem Gespräch eine Anleitung fürs Wiki. ',
};

const defaultCatalog: EntityPromptCatalog = {
  quickPrompts: ['Was kann KIAN in diesem Portal?', 'Ticket anlegen: ', 'Meine offenen Submissions kurz erklären'],
  slash: [
    { cmd: 'help', label: 'Hilfe', template: 'Welche Dinge kannst du mir im Techniker-Portal konkret helfen?' },
    { cmd: 'kb', label: 'Wissensbasis', template: 'Verweise auf relevante How-Tos oder Checklisten, wo sinnvoll.' },
    createTicketSlash,
    wikiSlash,
  ],
};

export const KIAN_PROMPT_CATALOG: Record<string, EntityPromptCatalog> = {
  'cw-ticket': {
    quickPrompts: ['Ticket zusammenfassen', 'Nächste 3 Schritte', 'Kundennotiz-Entwurf', 'Fehlermuster / Archive-Hinweise'],
    slash: ticketSlash,
  },
  'weclapp-ticket': {
    quickPrompts: [
      'Ticket zusammenfassen',
      'Nächste 3 Schritte',
      'Setze das Ticket auf „wartet" — Blocker: wartet auf ',
      'Schließe das Ticket mit diesem Abschlusskommentar: ',
      'Kundennotiz-Entwurf',
    ],
    slash: [...ticketSlash, ...ticketActionSlash, wikiSlash],
  },
  submission: {
    quickPrompts: [
      'Problembeschreibung verbessern',
      'Lösungsvorschläge nachschlagen',
      'Maßnahmen-Text polieren',
      'Plausibilitätscheck des Reports',
    ],
    slash: [
      { cmd: 'improve', label: 'Text verbessern', template: 'Verbessere die Problembeschreibung: klarer, technischer, ohne Floskeln.' },
      { cmd: 'kb', label: 'Wissensbasis', template: 'Welche KB-Artikel oder Checklisten passen zum beschriebenen Problem?' },
      { cmd: 'check', label: 'Plausibilität', template: 'Prüfe den Service-Report auf Lücken, Widersprüche und fehlende Infos.' },
    ],
  },
  'submission-delivery': {
    quickPrompts: ['Empfängerbestätigung formulieren', 'Seriennummern / Mengen checken', 'Lieferhinweise für den Kunden'],
    slash: [
      { cmd: 'check', label: 'Validierung', template: 'Prüfe Lieferung / Seriennummern / Mengen auf Plausibilität.' },
      { cmd: 'labels', label: 'Etiketten', template: 'Kurze Hinweise zu Etiketten, Verpackung oder Übergabe an den Kunden.' },
    ],
  },
  'infrastructure-host': {
    quickPrompts: ['Letzte Änderungen skizzieren', 'Typische Probleme dieses Typs', 'Config-Best-Practices'],
    slash: [
      { cmd: 'history', label: 'Historie', template: 'Was könnte an diesem Gerät / dieser Config zuletzt relevant gewesen sein?' },
      { cmd: 'kb', label: 'Wissensbasis', template: 'Passende KB-Hinweise zu diesem Gerätetyp oder dieser Rolle.' },
      {
        cmd: 'similar',
        label: 'Ähnliche Fälle',
        template: 'Typische Störungen oder Fehlerbilder bei ähnlicher Infrastruktur.',
        hints: { forceSimilar: true },
      },
    ],
  },
  'infrastructure-site': {
    quickPrompts: ['Standort / Raum überblicken', 'Typische Ausfallursachen', 'Dokumentationslücken'],
    slash: [
      { cmd: 'history', label: 'Historie', template: 'Strukturierte Übersicht, was an diesem Standort/Raum wichtig ist.' },
      { cmd: 'kb', label: 'Wissensbasis', template: 'Relevante KB-Artikel für diesen Infrastruktur-Kontext.' },
    ],
  },
  'inventory-session': {
    quickPrompts: ['Unstimmigkeiten zusammenfassen', 'Fehlbestände priorisieren', 'Nächste Inventur-Schritte'],
    slash: [
      { cmd: 'diff', label: 'Differenzen', template: 'Fasse auffällige Differenzen und mögliche Ursachen zusammen.' },
      { cmd: 'next', label: 'Nächste Schritte', template: 'Empfohlene nächste Schritte für diese Inventur-Session.' },
    ],
  },
  trip: {
    quickPrompts: ['Fahrtenbuch-Text vorschlagen', 'Spesen / Kilometer plausibel?', 'Formulierung für Admin-Korrektur'],
    slash: [
      { cmd: 'draft', label: 'Fahrtenbuch', template: 'Kurzer sachlicher Fahrtenbuch-Text für diese Fahrt.' },
      { cmd: 'check', label: 'Plausibilität', template: 'Prüfe Kilometer und Zeiten auf Plausibilität.' },
    ],
  },
  'new-ticket-draft': {
    quickPrompts: ['Ticket-Titel vorschlagen', 'Beschreibung strukturieren', 'Kategorie/Priorität einschätzen'],
    slash: [
      { cmd: 'draft', label: 'Entwurf', template: 'Mache aus der Beschreibung einen kurzen Betreff und eine strukturierte Ticketbeschreibung (Problem, Kontext, Ziel).' },
      { cmd: 'classify', label: 'Einordnung', template: 'Schlage Priorität, Typ und kurze Tags vor (nur Vorschlag).' },
    ],
  },
  customer: {
    quickPrompts: ['Kundenüberblick', 'Offene Tickets des Kunden', 'Leg für diesen Kunden ein Ticket an: ', 'Nächste Kommunikation'],
    slash: [
      { cmd: 'summary', label: 'Überblick', template: 'Kurzer Überblick zum Kundenkontext und sinnvolle nächste Schritte.' },
      { cmd: 'open', label: 'Offene Tickets', template: 'Welche offenen Tickets hat dieser Kunde? Was ist am dringendsten?' },
      { ...createTicketSlash, template: 'Leg für diesen Kunden ein Ticket an: ' },
      { cmd: 'kb', label: 'Wissensbasis', template: 'Relevante allgemeine Hinweise aus der KB für diesen Kundenkontext.' },
      {
        ...wikiSlash,
        template: 'Mach aus diesem Gespräch eine Anleitung fürs Wiki — nur für diesen Kunden, falls die Lösung an seiner Umgebung hängt. ',
      },
    ],
  },
  project: {
    quickPrompts: [
      'Projektstand zusammenfassen',
      'Offene und überfällige Aufgaben',
      'Leg zu diesem Projekt ein Ticket an: ',
      'Risiken und nächste Schritte',
    ],
    slash: [
      { cmd: 'summary', label: 'Stand', template: 'Fasse den Projektstand zusammen: Fortschritt, nächste Meilensteine, offene Punkte.' },
      { cmd: 'overdue', label: 'Überfällig', template: 'Welche Aufgaben sind überfällig oder bald fällig, und wer ist dran?' },
      { ...createTicketSlash, template: 'Leg zu diesem Projekt ein Ticket beim Projektkunden an: ' },
      { cmd: 'next', label: 'Nächste Schritte', template: 'Welche nächsten Schritte empfiehlst du für dieses Projekt?' },
    ],
  },
  appointments: {
    quickPrompts: ['Heutige Termine überblicken', 'Einsatz vorbereiten', 'Route / Anfahrt planen'],
    slash: [
      { cmd: 'prep', label: 'Einsatz vorbereiten', template: 'Was sollte ich vor diesem Einsatz wissen oder vorbereiten?' },
      { cmd: 'route', label: 'Anfahrt', template: 'Kurze Hinweise zur Anfahrt oder zum Standort des Einsatzes.' },
    ],
  },
};

export function getPromptCatalog(entityType: string | null | undefined): EntityPromptCatalog {
  if (!entityType) return defaultCatalog;
  return KIAN_PROMPT_CATALOG[entityType] ?? defaultCatalog;
}

export function getAllSlashForType(entityType: string | null | undefined): SlashCommandDef[] {
  const c = getPromptCatalog(entityType);
  const merged = [...c.slash];
  if (entityType && entityType !== 'general') {
    for (const d of defaultCatalog.slash) {
      if (!merged.some((m) => m.cmd === d.cmd)) merged.push(d);
    }
  }
  return merged;
}
